import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { SituacaoChavePix } from '../../api';

/**
 * Conferência da chave Pix da conta ASAAS, no cadastro da forma de pagamento.
 *
 * Sem chave Pix ativa o ASAAS recusa a cobrança inteira e o cliente final lê
 * "Não há nenhuma chave Pix disponível para receber cobranças" no checkout —
 * uma frase sobre a conta do LOJISTA, mostrada para quem não pode fazer nada a
 * respeito. Este aviso leva o problema para quem resolve, antes de ir ao ar.
 *
 * Não bloqueia o salvamento: a conferência é uma chamada ao ASAAS e pode falhar
 * por rede ou instabilidade — travar a tela por isso trocaria um problema de
 * fora por um impedimento aqui dentro.
 */
export function AlertaChavePix({
  situacao,
  aoVerificarDeNovo,
  verificando,
}: {
  situacao: SituacaoChavePix;
  aoVerificarDeNovo: () => void;
  verificando: boolean;
}): JSX.Element | null {
  // Tudo certo: nenhum aviso. Confirmação verde aqui viraria mais um cartaz para
  // ler numa tela que já tem muito texto.
  if (situacao === 'ATIVA') return null;

  if (situacao === 'PENDENTE') {
    return (
      <Alert severity="info" variant="outlined">
        <AlertTitle>A chave Pix ainda está sendo ativada</AlertTitle>
        <Typography variant="body2">
          A conta ASAAS desta loja já tem uma chave Pix cadastrada, mas o Banco Central ainda não terminou de
          liberá-la — costuma levar alguns minutos. Até lá, quem escolher Pix no cardápio vai receber um erro
          na hora de pagar. Pode salvar esta forma de pagamento agora e conferir de novo em instantes.
        </Typography>
        <BotaoVerificar aoVerificarDeNovo={aoVerificarDeNovo} verificando={verificando} />
      </Alert>
    );
  }

  if (situacao === 'NAO_VERIFICADO') {
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>Não foi possível conferir a chave Pix</AlertTitle>
        <Typography variant="body2">
          O ASAAS não respondeu à conferência. <b>Isso não quer dizer que falta a chave</b> — pode ser
          instabilidade momentânea. Você pode salvar normalmente. Se o Pix falhar no cardápio, volte aqui e
          verifique de novo.
        </Typography>
        <BotaoVerificar aoVerificarDeNovo={aoVerificarDeNovo} verificando={verificando} />
      </Alert>
    );
  }

  return (
    <Alert severity="error" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
      <AlertTitle>A conta ASAAS desta loja não tem chave Pix</AlertTitle>
      <Typography variant="body2">
        Do jeito que está, o cliente escolhe Pix no cardápio, chega até o fim do pedido e <b>não recebe o QR
        Code</b>: aparece um erro na hora de pagar. A chave é cadastrada na conta ASAAS da loja, não aqui.
        Para cadastrar:
      </Typography>

      <Box component="ol" sx={{ pl: 2.5, mt: 1, mb: 0, '& li': { mb: 0.75 }, typography: 'body2' }}>
        <li>
          Entre na conta <b>ASAAS</b> da loja e abra o menu <b>Pix</b>.
        </li>
        <li>
          Em <b>Minhas chaves</b>, clique em <b>Nova chave</b>.
        </li>
        <li>
          Escolha o tipo <b>Chave aleatória</b> — é a que não depende de CPF, e-mail ou telefone já usados em
          outro banco.
        </li>
        <li>
          Confirme e volte aqui. Se o ASAAS não deixar criar a chave, é porque a conta ainda está em análise:
          termine o envio dos documentos primeiro.
        </li>
      </Box>

      <BotaoVerificar aoVerificarDeNovo={aoVerificarDeNovo} verificando={verificando} />
    </Alert>
  );
}

function BotaoVerificar({
  aoVerificarDeNovo,
  verificando,
}: {
  aoVerificarDeNovo: () => void;
  verificando: boolean;
}): JSX.Element {
  return (
    <Button
      size="small"
      startIcon={<RefreshIcon />}
      onClick={aoVerificarDeNovo}
      disabled={verificando}
      sx={{ mt: 1, px: 0 }}
      color="inherit"
    >
      {verificando ? 'Verificando…' : 'Já cadastrei — verificar de novo'}
    </Button>
  );
}
